import React, { memo } from 'react';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useIsMobile } from '@/hooks/ui';
import ScheduleOverviewCard from '@/components/employees/ScheduleOverviewCard';

// Import unified components
import UnifiedShiftProvider, { useUnifiedShift, useShiftManagement } from './context/UnifiedShiftProvider';
import ShiftCalendarContainer from './calendar/ShiftCalendarContainer';
import { ShiftSchedulingActivityLogCard } from './ShiftSchedulingActivityLogCard';
// Removed realtime functionality

interface ShiftManagementTabProps {
  businessId: string | null;
}



// Inner component that uses the ShiftData context
const ShiftManagementContent: React.FC = memo(() => {
  const {
    selectedBusinessDetails,
    periodStatuses,
    plannedShifts,
    allShiftsAssigned,
    periodStatusesLoading,
    loadingOpeningHours,
    periodStatusesError,
    refreshKey,
    businessId,
    employees
  } = useUnifiedShift();

  // Calendar navigation state and actions for month synchronization
  const {
    currentDisplayMonth,
    handleNextMonth,
    handlePreviousMonth,
    handleMonthSelect,
    selectShift,
    clearShiftSelection,
  } = useShiftManagement();

  const isMobile = useIsMobile();
  const { selectedShiftDate, setSelectedShiftDate } = useDashboardTab();

  // Handler to highlight shift from activity log
  const handleShiftClick = (shiftId: string, shiftDate: Date) => {
    // Clear any existing selection and select the clicked shift
    clearShiftSelection();
    selectShift(shiftId, true);

    // Navigate to the shift's date/month if needed
    if (shiftDate) {
      handleMonthSelect(shiftDate);
      setSelectedShiftDate(shiftDate);
    }
  };

  // Handle schedule overview actions
  const handleSetActiveView = (view: string) => {
    // We're already in shift management, so no navigation needed
    // Day selection is handled by the existing day click handler through the context
  };

  return (
    <div className="h-full flex flex-col min-h-0 space-y-4">
      {/* Schedule Overview Card for quick day navigation */}
      <ScheduleOverviewCard
        setActiveView={handleSetActiveView}
        businessDetails={selectedBusinessDetails}
        periodStatuses={periodStatuses}
        plannedShifts={plannedShifts}
        allShiftsAssigned={allShiftsAssigned}
        refreshKey={refreshKey}
        isLoading={periodStatusesLoading && !periodStatusesError}
        currentViewMonth={currentDisplayMonth}
        onNextMonth={handleNextMonth}
        onPreviousMonth={handlePreviousMonth}
        onMonthSelect={handleMonthSelect}
        businessCreatedAt={selectedBusinessDetails?.created_at}
      />


      {/* Shift Scheduling Activity Log */}
      {businessId && (
        <ShiftSchedulingActivityLogCard
          businessId={businessId}
          selectedDate={currentDisplayMonth}
          employees={employees}
          className="flex-shrink-0"
          onShiftClick={handleShiftClick}
        />
      )}

      {/* Calendar Container */}
      <div className="flex-1 min-h-0">
        <ShiftCalendarContainer />
      </div>

    </div>
  );
});

ShiftManagementContent.displayName = 'ShiftManagementContent';

const ShiftManagementTab: React.FC<ShiftManagementTabProps> = ({ businessId }) => {
  const { setSelectedShiftDate } = useDashboardTab();

  return (
    <UnifiedShiftProvider
      businessId={businessId}
      onSelectedDateChange={setSelectedShiftDate}
      infiniteScrollEnabled={true}
    >
      <ShiftManagementContent />
    </UnifiedShiftProvider>
  );
};


export default ShiftManagementTab;
